/**
 * The preview-only support fixture capability — Task 15.1 and 15.2, requirements 13.1–13.8.
 *
 * A preview smoke run needs to submit one real support request and then remove exactly what it made.
 * It cannot do that by picking a row out of the queue afterwards: the queue is shared, the request it
 * created is indistinguishable from a customer's, and "delete the newest one" is how a smoke test
 * eventually deletes somebody's actual complaint.
 *
 * So the run declares its intent *before* it submits. It registers a one-use pairing of the
 * `submissionId` it is about to send with the `testRunId` it wants that request marked as, intake
 * consumes the registration atomically, and cleanup deletes only rows carrying that marker. Nothing
 * about an ordinary submission changes, and no ordinary row is ever reachable through these routes —
 * a request with a null `test_run_id` cannot be selected by a cleanup keyed on one.
 *
 * ## Both identifiers are UUIDs the caller generates
 *
 * The `submissionId` is the one intake already keys idempotency on, so registration is a claim about a
 * submission that does not exist yet. The `testRunId` is the marker, and it is a UUID rather than a
 * readable label on purpose: a label like `nightly-smoke` would be guessable, and a guessable marker
 * is a delete anyone can aim. Both columns are `uuid` in the schema, which rejects anything else
 * before it reaches a query.
 *
 * ## Nothing here carries PII
 *
 * The request bodies and the responses are identifiers and a timestamp. The name, email, and message
 * of the submission the run is about to make never pass through this capability, and the responses
 * deliberately do not echo a submission's content, its opaque public id, or a count of anything but
 * the run's own rows. A fixture endpoint that answered with a support request's fields would be an
 * unauthenticated read of customer data wearing a test harness's clothes.
 */
import { z } from 'zod';
import { supportOpaqueIdSchema } from './common.js';
/** The one path, on the admin surface only. Trailing-slash and case variants are not this path. */
export const previewFixturePath = '/api/__e2e/support-fixtures';
/**
 * The header the shared token travels in.
 *
 * Not `Authorization`, deliberately, and the guard rejects that header outright. `Authorization` is
 * what a *session* would arrive in, and this capability must be unable to accept ambient authority —
 * so the token has a name of its own and the credential headers are a rejection rather than an
 * alternative.
 */
export const previewFixtureTokenHeader = 'x-e2e-fixture-token';
/**
 * How long an unconsumed registration stays usable — Task 15.2.
 *
 * Ten minutes is longer than any smoke run's gap between registering and submitting, and short enough
 * that an abandoned run's registration cannot be picked up by a later one. Expiry is what keeps a
 * crashed run from leaving a usable claim behind.
 */
export const previewFixtureTtlMinutes = 10;
export const previewFixtureTtlMilliseconds = previewFixtureTtlMinutes * 60 * 1_000;
/** What a run registers before it submits. */
export const previewFixtureRegistrationInputSchema = z
    .object({
    submissionId: supportOpaqueIdSchema,
    testRunId: supportOpaqueIdSchema,
})
    .strict();
/** The answer to a registration: the marker and when the claim stops being usable. */
export const previewFixtureRegistrationSchema = z
    .object({
    testRunId: supportOpaqueIdSchema,
    expiresAt: z.iso.datetime({ offset: false }),
    status: z.literal('registered'),
})
    .strict();
/**
 * The answer to a cleanup: how much of *this run's* work was removed.
 *
 * Two counts rather than one, because they mean different things. `requestsDeleted` is the durable
 * support rows the run created; `registrationsDeleted` is claims it registered and never used. A run
 * that submitted successfully sees one and zero, and a run that crashed before submitting sees zero
 * and one — which is the difference between "cleanup worked" and "there was nothing to clean".
 *
 * The names are the store's own (`SupportFixtureRunCleanup`), so the wire and the persistence boundary
 * do not need a translation nobody would remember to keep correct.
 */
export const previewFixtureCleanupSchema = z
    .object({
    testRunId: supportOpaqueIdSchema,
    requestsDeleted: z.number().int().nonnegative(),
    registrationsDeleted: z.number().int().nonnegative(),
    status: z.literal('deleted'),
})
    .strict();
/**
 * Every field either response carries, for the test that asserts the surface is only identifiers.
 *
 * Derived from the schemas rather than retyped, so widening one without deciding about it fails.
 */
export const previewFixtureResponseFields = Object.freeze([
    ...new Set([
        ...Object.keys(previewFixtureRegistrationSchema.shape),
        ...Object.keys(previewFixtureCleanupSchema.shape),
    ]),
]);
//# sourceMappingURL=fixtures.js.map